import type { ReactElement } from 'react';
import { forwardRef } from 'react';
import { Box, Icon, useTheme } from '@chakra-ui/react';
import pick from 'lodash-es/pick';
import type { IconProps } from '@chakra-ui/react';
import AccountSymbol from '../../assets/svg/account.svg';
// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
import AfterPaySymbol from '../../assets/svg/afterpay.svg';
import '../../assets/svg/alert.svg';
import ApplePaySymbol from '../../assets/svg/apple-pay.svg';
import ArrowLeftSymbol from '../../assets/svg/arrow-left.svg';
import ArrowRightSymbol from '../../assets/svg/arrow-right.svg';
import BasketSymbol from '../../assets/svg/basket.svg';
import BlackLockSymbol from '../../assets/svg/black-lock.svg';
import BrandLogoSymbol from '../../assets/svg/brand-logo.svg';
import CalendarSymbol from '../../assets/svg/calendar.svg';
import '../../assets/svg/check-circle-green.svg';
import '../../assets/svg/check-circle.svg';
import '../../assets/svg/check.svg';
import ChefSymbol from '../../assets/svg/chef.svg';
import ChevronDownBigSymbol from '../../assets/svg/chevron-down-big.svg';
import '../../assets/svg/chevron-down.svg';
import '../../assets/svg/chevron-left.svg';
import ChevronRightSymbol from '../../assets/svg/chevron-right.svg';
import ChevronUpBigSymbol from '../../assets/svg/chevron-up-big.svg';
import '../../assets/svg/chevron-up.svg';
import '../../assets/svg/circle-arrow-north-east.svg';
import '../../assets/svg/circle-check.svg';
import CircleChevronSymbol from '../../assets/svg/circle-chevron-right.svg';
import CircleCloseSymbol from '../../assets/svg/circle-close.svg';
import CloseSymbol from '../../assets/svg/close.svg';
import ContactSymbol from '../../assets/svg/contact.svg';
import CookingClassSymbol from '../../assets/svg/cooking-class.svg';
import CookingClassesSymbol from '../../assets/svg/cooking-classes.svg';
import AmexSymbol from '../../assets/svg/credit-card-american-express.svg';
import CreditCardBackSymbol from '../../assets/svg/credit-card-back.svg';
import DiscoverSymbol from '../../assets/svg/credit-card-discover.svg';
import MasterCardSymbol from '../../assets/svg/credit-card-master-card.svg';
import VisaSymbol from '../../assets/svg/credit-card-visa.svg';
import DeliverySameDaySymbol from '../../assets/svg/delivery-same-day.svg';
import DeliverySymbol from '../../assets/svg/delivery.svg';
import '../../assets/svg/edit.svg';
import '../../assets/svg/empty-star.svg';
import EventsSymbol from '../../assets/svg/events.svg';
import FacebookSymbol from '../../assets/svg/facebook.svg';
import '../../assets/svg/file.svg';
import '../../assets/svg/gift2.svg';
import GiftRegistryWithHeartSymbol from '../../assets/svg/gift-registry-with-heart.svg';
import '../../assets/svg/gift-registry.svg';
import '../../assets/svg/gift.svg';
import HamburgerSymbol from '../../assets/svg/hamburger.svg';
import InStorePickupSymbol from '../../assets/svg/in-store-pickup.svg';
import InfoSymbol from '../../assets/svg/info.svg';
import '../../assets/svg/input-plus.svg';
import InstagramSymbol from '../../assets/svg/instagram.svg';
import LetterEmailSymbol from '../../assets/svg/letter-email.svg';
import LocationSymbol from '../../assets/svg/location.svg';
import LockSymbol from '../../assets/svg/lock.svg';
import '../../assets/svg/manage-wishlist.svg';
import '../../assets/svg/minus-icon.svg';
import OrderHistorySymbol from '../../assets/svg/order-history.svg';
import OrderSymbol from '../../assets/svg/order.svg';
import Paypal2Symbol from '../../assets/svg/paypal-2.svg';
import PaypalSymbol from '../../assets/svg/paypal.svg';
import PerksNotFilledSymbol from '../../assets/svg/perks-not-filled.svg';
import PerksSymbol from '../../assets/svg/perks.svg';
import PhoneSymbol from '../../assets/svg/phone.svg';
import PinterestSymbol from '../../assets/svg/pinterest.svg';
import Plus2Symbol from '../../assets/svg/plus2.svg';
import '../../assets/svg/plus.svg';
import RecipesSymbol from '../../assets/svg/recipes.svg';
import SafoniaBranchSymbol from '../../assets/svg/safonia-branch.svg';
import SafoniaHeadsetSymbol from '../../assets/svg/safonia-headset.svg';
import SafoniaColoredSymbol from '../../assets/svg/safonia-logo-colored.svg';
import SafoniaNameSymbol from '../../assets/svg/safonia-logo-name.svg';
import SafoniaLogoSymbol from '../../assets/svg/safonia-logo.svg';
import SafoniaShippingSymbol from '../../assets/svg/safonia-shipping.svg';
import SameDayDeliverySymbol from '../../assets/svg/same-day-delivery.svg';
import SavePaymentSymbol from '../../assets/svg/save-payment.svg';
import SearchSymbol from '../../assets/svg/search.svg';
import ShippingSymbol from '../../assets/svg/shipping.svg';
import '../../assets/svg/signout.svg';
import '../../assets/svg/star.svg';
import StarsSymbol from '../../assets/svg/stars.svg';
import StoreSymbol from '../../assets/svg/store.svg';
import '../../assets/svg/surlatable-mini.svg';
import TimeSymbol from '../../assets/svg/time.svg';
import '../../assets/svg/trash.svg';
import UserSymbol from '../../assets/svg/user.svg';
import '../../assets/svg/view-off.svg';
import '../../assets/svg/view.svg';
import '../../assets/svg/visibility-off.svg';
import '../../assets/svg/visibility.svg';
import '../../assets/svg/warning.svg';
import WhiskSymbol from '../../assets/svg/whisk.svg';
import WishListActionFilledSymbol from '../../assets/svg/wish-list-action-filled.svg';
import WishListActionSymbol from '../../assets/svg/wish-list-action.svg';
import WishListSymbol from '../../assets/svg/wish-list.svg';
import '../../assets/svg/x-circle.svg';
import '../../pages/checkout/login/images/checkout-bag.svg';
import '../../pages/checkout/login/images/hide-password.svg';
import '../../pages/checkout/login/images/login-info.svg';
import '../../pages/checkout/login/images/show-password.svg';
// Our own SVG imports. These will be extracted to a single sprite sheet by the
// svg-sprite-loader webpack plugin at build time and injected in the <body> tag
// during SSR.
// NOTE: Another solution would be to use `require-context.macro` package to accomplish
// importing icon svg's.
import '../stepper/images/completed-check.svg';
import { IconMap } from './Icon.map';
import { IconsList } from './icon-list-loader';
import type { GetIconProps, IconMapProps } from './types';

ApplePaySymbol.viewBox = ApplePaySymbol.viewBox || '0 0 51 22';
AfterPaySymbol.viewBox = AfterPaySymbol.viewBox || '0 0 74 15';
BrandLogoSymbol.viewBox = BrandLogoSymbol.viewBox || '0 0 215 49';
PhoneSymbol.viewBox = PhoneSymbol.viewBox || '0 0 16 16';
BasketSymbol.viewBox = BasketSymbol.viewBox || '0 0 24 24';
PaypalSymbol.viewBox = PaypalSymbol.viewBox || '0 0 80 20';
Paypal2Symbol.viewBox = Paypal2Symbol.viewBox || '0 0 65 20';
UserSymbol.viewBox = UserSymbol.viewBox || '0 0 24 24';
WishListSymbol.viewBox = WishListSymbol.viewBox || '0 0 24 24';
WishListActionSymbol.viewBox = WishListActionSymbol.viewBox || '0 0 24 24';
WishListActionFilledSymbol.viewBox = WishListActionFilledSymbol.viewBox || '0 0 24 24';
LocationSymbol.viewBox = LocationSymbol.viewBox || '0 0 14 16';
SearchSymbol.viewBox = SearchSymbol.viewBox || '0 0 24 24';
CloseSymbol.viewBox = CloseSymbol.viewBox || '0 0 18 18';
ContactSymbol.viewBox = ContactSymbol.viewBox || '0 0 16 12';

/**
 * A helper for creating a Chakra-wrapped icon from our own SVG imports via sprite sheet.
 * @param {string} name - the filename of the imported svg (does not include extension)
 */
/* istanbul ignore next */
export const icon = (name: string, passProps?: IconProps, outerWidth = '', outerHeight = '') => {
    const displayName = name
        .toLowerCase()
        .replace(/(?:^|[\s-/])\w/g, (match) => match.toUpperCase())
        .replace(/-/g, '');
    const component = forwardRef<typeof Icon, IconProps>((props, ref) => {
        const theme = useTheme();

        // FIXME: When updating chakra, it will provide better typing, remove the eslint-disable - still not fixed in chakra v2
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
        const { baseStyle } = theme.components.Icon;
        return (
            <div
                style={{
                    ...(outerWidth ? { width: outerWidth } : {}),
                    ...(outerHeight ? { height: outerHeight } : {}),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Icon ref={ref} {...baseStyle} {...passProps} {...props}>
                    <use role="presentation" xlinkHref={`#${name}`} />
                </Icon>
            </div>
        );
    });
    component.displayName = `${displayName}Icon`;
    return component;
};

interface ToIconArgs {
    name: string;
    symbol: { viewBox: string };
    defaultViewBox?: string;
    passProps?: IconProps;
    outerWidth?: string;
    outerHeight?: string;
}

type TI = (...args: [ToIconArgs]) => ReturnType<typeof icon>;

const toIcon: TI = ({ name, symbol, defaultViewBox, passProps, outerWidth, outerHeight }) => {
    symbol.viewBox = symbol.viewBox ?? defaultViewBox ?? '0 0 16 16';
    const [, , vbWidth, vbHeight] = symbol.viewBox.split(' ');
    return icon(
        name,
        {
            viewBox: symbol.viewBox,
            width: vbWidth,
            height: vbHeight,
            ...(passProps ?? {}),
        },
        outerWidth,
        outerHeight,
    );
};

export const symbolToIcon = (
    symbol: { viewBox: string },
    name: string,
    passProps?: IconProps,
): ReturnType<typeof icon> =>
    icon(name, {
        ...(passProps ?? {}),
        viewBox: symbol.viewBox,
    });

// Export Chakra icon components that use our SVG sprite symbol internally
// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
export const AlertIcon = icon('alert');
export const AccountIcon = toIcon({
    name: 'account',
    symbol: AccountSymbol,
    defaultViewBox: '0 0 26 20',
    passProps: { width: '26px', height: '20px' },
});
export const BrandLogo = symbolToIcon(BrandLogoSymbol, 'brand-logo', {
    color: 'slt-pepper.1000',
    width: '215px',
    height: '49px',
});
export const BasketIcon = toIcon({
    name: 'basket',
    symbol: BasketSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px', color: 'slt-pepper.1000' },
});
export const PhoneIcon = symbolToIcon(PhoneSymbol, 'phone', {
    color: 'black',
    width: 4,
    height: 4,
});
export const CheckIcon = icon('check');
export const CheckCircleIcon = icon('check-circle');
export const CheckCircleGreenIcon = icon('check-circle-green');
export const ChevronDownIcon = icon('chevron-down');
export const ChevronLeftIcon = icon('chevron-left');
export const ChevronUpIcon = icon('chevron-up');
export const ChevronUpBigIcon = toIcon({
    name: 'chevron-up-big',
    symbol: ChevronUpBigSymbol,
    defaultViewBox: '0 0 12 6',
    passProps: { width: '12px', height: '6px' },
});
export const ChevronDownBigIcon = toIcon({
    name: 'chevron-down-big',
    symbol: ChevronDownBigSymbol,
    defaultViewBox: '0 0 12 6',
    passProps: { width: '12px', height: '6px' },
});
export const CircleCheckIcon = icon('circle-check');
export const CircleArrowNorthEastIcon = icon('circle-arrow-north-east');
const creditCard = {
    defaultViewBox: '0 0 64 44',
    passProps: {
        width: '64px',
        height: '44px',
    },
};
export const CreditCardAmericanExpressIcon = toIcon({
    ...creditCard,
    name: 'credit-card-american-express',
    symbol: AmexSymbol,
});
export const CreditCardDiscoverIcon = toIcon({
    ...creditCard,
    name: 'credit-card-discover',
    symbol: DiscoverSymbol,
});
export const CreditCardMasterCardIcon = toIcon({
    ...creditCard,
    name: 'credit-card-master-card',
    symbol: MasterCardSymbol,
});
export const CreditCardVisaIcon = toIcon({
    ...creditCard,
    name: 'credit-card-visa',
    symbol: VisaSymbol,
});
export const CreditCardBackIcon = toIcon({
    ...creditCard,
    name: 'credit-card-back',
    symbol: CreditCardBackSymbol,
});
export const FileIcon = icon('file');
export const HamburgerIcon = toIcon({
    name: 'hamburger',
    symbol: HamburgerSymbol,
    defaultViewBox: '0 0 24 22',
    passProps: {
        width: '24px',
        height: '22px',
    },
});

export const InfoIcon = toIcon({
    name: 'info',
    symbol: InfoSymbol,
    defaultViewBox: '0 0 16 16',
    passProps: {
        width: '16px',
        height: '16px',
    },
});
export const LockIcon = toIcon({
    name: 'lock',
    symbol: LockSymbol,
    defaultViewBox: '0 0 20 24',
    passProps: {
        width: '20px',
        height: '24px',
    },
});
export const BlackLockIcon = toIcon({
    name: 'black-lock',
    symbol: BlackLockSymbol,
    defaultViewBox: '0 0 20 24',
    passProps: {
        width: '20px',
        height: '24px',
    },
});
export const PaypalIcon = icon('paypal', { viewBox: PaypalSymbol.viewBox, width: '100%' });
export const PlusIcon = icon('plus');
export const SignoutIcon = icon('signout');
export const WarningIcon = icon('warning');

export const Star = icon('star');

export const EmptyStar = icon('empty-star');
export const ViewIcon = icon('view');
export const ViewOffIcon = icon('view-off');
export const VisibilityIcon = icon('visibility');
export const VisibilityOffIcon = icon('visibility-off');
export const HeartIcon = icon('heart');
export const HeartSolidIcon = icon('heart-solid');
export const ApplePayIcon = toIcon({
    name: 'apple-pay',
    symbol: ApplePaySymbol,
    defaultViewBox: '0 0 51 22',
    passProps: {
        width: '51px',
        height: '22px',
    },
});
export const AfterPayIcon = toIcon({
    name: 'afterpay',
    symbol: AfterPaySymbol,
    defaultViewBox: '0 0 98 24',
    passProps: {
        width: '98px',
        height: '24px',
    },
});
export const Paypal2Icon = toIcon({
    name: 'paypal-2',
    symbol: Paypal2Symbol,
    defaultViewBox: '0 0 65 20',
    passProps: {
        width: '65px',
        height: '20px',
    },
});
export const Plus2Icon = toIcon({
    name: 'plus2',
    symbol: Plus2Symbol,
    defaultViewBox: '0 0 17 16',
    passProps: {
        width: '17px',
        height: '16px',
    },
});
export const EditIcon = icon('edit');
export const GiftIcon = icon('gift');
export const TimeIcon = toIcon({
    name: 'time',
    symbol: TimeSymbol,
    defaultViewBox: '0 0 16 16',
    passProps: { width: '16px', height: '16px' },
});
export const GiftRegistryIcon = icon('gift-registry');
export const GiftRegistryWithHeartIcon = toIcon({
    name: 'gift-registry-with-heart',
    symbol: GiftRegistryWithHeartSymbol,
    defaultViewBox: '0 0 31 33',
    passProps: { width: '31px', height: '33px' },
});
export const ManageWishlistIcon = icon('manage-wishlist');

export const OrderIcon = toIcon({
    name: 'order',
    symbol: OrderSymbol,
    defaultViewBox: '0 0 26 20',
    passProps: { width: '26px', height: '20px' },
});

export const OrderHistoryIcon = toIcon({
    name: 'order-history',
    symbol: OrderHistorySymbol,
    defaultViewBox: '0 0 33 33',
    passProps: { width: '33px', height: '33px' },
});

export const SavePaymentIcon = toIcon({
    name: 'save-payment',
    symbol: SavePaymentSymbol,
    defaultViewBox: '0 0 33 33',
    passProps: { width: '33px', height: '33px' },
});
export const TrashIcon = icon('trash');
export const LocationIcon = toIcon({
    name: 'location',
    symbol: LocationSymbol,
    defaultViewBox: '0 0 14 16',
    passProps: { width: '14px', height: '16px' },
});

export const CircleChevronRightIcon = toIcon({
    name: 'circle-chevron-right',
    symbol: CircleChevronSymbol,
    defaultViewBox: '0 0 32 32',
    passProps: {
        width: '32px',
        height: '32px',
    },
});
export const ChevronRightIcon = toIcon({
    name: 'chevron-right',
    symbol: ChevronRightSymbol,
    defaultViewBox: '0 0 8 14',
    passProps: {
        width: '8px',
        height: '14px',
    },
});
export const CookingClassIcon = toIcon({
    name: 'cooking-class',
    symbol: CookingClassSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: {
        width: '24px',
        height: '24px',
    },
});

export const CookingClassesIcon = toIcon({
    name: 'cooking-classes',
    symbol: CookingClassesSymbol,
    defaultViewBox: '0 0 16 16',
    passProps: {
        width: '16px',
        height: '16px',
    },
});

export const RecipesIcon = toIcon({
    name: 'recipes',
    symbol: RecipesSymbol,
    defaultViewBox: '0 0 16 16',
    passProps: {
        width: '16px',
        height: '16px',
    },
});

export const SafoniaLogoIconSmall = toIcon({
    name: 'safonia-logo-small',
    symbol: SafoniaLogoSymbol,
    defaultViewBox: '0 0 15 16',
    passProps: {
        width: '15px',
        height: '16px',
    },
});

export const SafoniaLogoIcon = toIcon({
    name: 'safonia-logo',
    symbol: SafoniaLogoSymbol,
    defaultViewBox: '0 0 15 16',
    passProps: {
        width: '15px',
        height: '16px',
    },
});

export const SafoniaNameIcon = toIcon({
    name: 'safonia-logo-name',
    symbol: SafoniaNameSymbol,
    defaultViewBox: '0 0 87 16',
    passProps: {
        width: '87px',
        height: '16px',
    },
});

export const SafoniaColoredIcon = toIcon({
    name: 'safonia-logo-colored',
    symbol: SafoniaColoredSymbol,
    defaultViewBox: '0 0 87 16',
    passProps: {
        width: '87px',
        height: '16px',
    },
});

export const SafoniaBranchIcon = toIcon({
    name: 'safonia-branch',
    symbol: SafoniaBranchSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px' },
});
export const SafoniaHeadsetIcon = toIcon({
    name: 'safonia-headset',
    symbol: SafoniaHeadsetSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px' },
});
export const SafoniaShippingIcon = toIcon({
    name: 'safonia-shipping',
    symbol: SafoniaShippingSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px' },
});

export const DeliveryIcon = toIcon({
    name: 'delivery',
    symbol: DeliverySymbol,
    defaultViewBox: '0 0 24 20',
    passProps: { height: '20px', width: '24px' },
});

export const DeliverySameDayIcon = toIcon({
    name: 'delivery-same-day',
    symbol: DeliverySameDaySymbol,
    defaultViewBox: '0 0 24 18',
    passProps: { height: '18px', width: '24px' },
});

export const Gift2Icon = icon('gift2');

export const MinusIcon = icon('minus-icon');
export const InputPlusIcon = icon('input-plus');

export const ShippingIcon = toIcon({
    name: 'shipping',
    symbol: ShippingSymbol,
    defaultViewBox: '0 0 24 16',
    passProps: { height: '16px', width: '24px' },
    outerWidth: '2rem',
    outerHeight: '2rem',
});

export const StoreIcon = toIcon({
    name: 'store',
    symbol: StoreSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { height: '24px', width: '24px' },
});

export const InStorePickupIcon = toIcon({
    name: 'in-store-pickup',
    symbol: InStorePickupSymbol,
    defaultViewBox: '0 0 18 20',
    passProps: { height: '20px', width: '18px' },
    outerWidth: '2rem',
    outerHeight: '2rem',
});

export const SameDayDeliveryIcon = toIcon({
    name: 'same-day-delivery',
    symbol: SameDayDeliverySymbol,
    defaultViewBox: '0 0 26 16',
    passProps: { height: '16px', width: '26px' },
    outerWidth: '2rem',
    outerHeight: '2rem',
});

export const SurLaTableMiniIcon = icon('surlatable-mini');
export const WishListIcon = toIcon({
    name: 'wish-list',
    symbol: WishListSymbol,
    defaultViewBox: '0 0 18 16',
    passProps: { width: '18px', height: '16px' },
});

export const UserIcon = toIcon({
    name: 'user',
    symbol: UserSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px' },
});

export const SearchIcon = toIcon({
    name: 'search',
    symbol: SearchSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px' },
});

export const CloseIcon = toIcon({
    name: 'close',
    symbol: CloseSymbol,
    defaultViewBox: '0 0 18 18',
    passProps: { width: '18px', height: '18px' },
});

export const CircleCloseIcon = toIcon({
    name: 'circle-close',
    symbol: CircleCloseSymbol,
    defaultViewBox: '0 0 24 25',
    passProps: { width: '24px', height: '25px' },
});

export const PinterestIcon = toIcon({
    name: 'pinterest',
    symbol: PinterestSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px' },
});

export const FacebookIcon = toIcon({
    name: 'facebook',
    symbol: FacebookSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px' },
});

export const InstagramIcon = toIcon({
    name: 'instagram',
    symbol: InstagramSymbol,
    defaultViewBox: '0 0 24 24',
    passProps: { width: '24px', height: '24px' },
});

export const ArrowLeftIcon = toIcon({
    name: 'arrow-left',
    symbol: ArrowLeftSymbol,
    defaultViewBox: '0 0 8 14',
    passProps: { width: '8px', height: '14px' },
});
export const ArrowRightIcon = toIcon({
    name: 'arrow-right',
    symbol: ArrowRightSymbol,
    defaultViewBox: '0 0 10 10',
    passProps: { width: '10px', height: '10px' },
});
export const ContactIcon = toIcon({
    name: 'contact',
    symbol: ContactSymbol,
    defaultViewBox: '0 0 16 12',
    passProps: { width: '16px', height: '12px' },
});
export const PerksIcon = toIcon({
    name: 'perks',
    symbol: PerksSymbol,
    defaultViewBox: '0 0 32 32',
    passProps: { width: '32px', height: '32px' },
});
export const PerksNotFilledIcon = toIcon({
    name: 'perks-not-filled',
    symbol: PerksNotFilledSymbol,
    defaultViewBox: '0 0 44 44',
    passProps: { width: '44px', height: '44px' },
});
export const StarsIcon = toIcon({
    name: 'stars',
    symbol: StarsSymbol,
    defaultViewBox: '0 0 44 44',
    passProps: { width: '44px', height: '44px' },
});
export const LetterEmailIcon = toIcon({
    name: 'letter-email',
    symbol: LetterEmailSymbol,
    defaultViewBox: '0 0 44 20',
    passProps: { width: '44px', height: '20px' },
});

export const CalendarIcon = toIcon({
    name: 'calendar',
    symbol: CalendarSymbol,
    defaultViewBox: '0 0 16 16',
    passProps: { width: '16px', height: '16px' },
});

export const XCircleIcon = icon('x-circle');

export const WhiskIcon = toIcon({
    name: 'whisk',
    symbol: WhiskSymbol,
    defaultViewBox: '0 0 10 34',
    passProps: { width: '10px', height: '34px' },
});

export const EventsIcon = toIcon({
    name: 'events',
    symbol: EventsSymbol,
    defaultViewBox: '0 0 32 32',
    passProps: {
        width: '32px',
        height: '32px',
    },
});

export const ChefIcon = toIcon({
    name: 'chef',
    symbol: ChefSymbol,
    defaultViewBox: '0 0 32 32',
    passProps: {
        width: '32px',
        height: '32px',
    },
});

export const GetIcon = ({
    id,
    name,
    onClick,
    size = 'sm',
    cursor,
    disableHover = false,
    color,
    ...props
}: GetIconProps): ReactElement | null => {
    const DynamicIcon = IconsList[name];
    const defaultIconProps: IconMapProps = IconMap[name] as IconMapProps;

    if (defaultIconProps) {
        const { width, height, _hover } = defaultIconProps[size];

        const boxProps = [
            'padding',
            'backgroundColor',
            'borderWidth',
            'borderColor',
            'borderRadius',
        ];

        return (
            <Box
                display="inline-block"
                {...pick(defaultIconProps[size], boxProps)}
                {...pick(props, boxProps)}
                borderStyle="solid"
                cursor={cursor}
                onClick={onClick}
                _hover={disableHover ? {} : _hover}
                data-testid={`${id ?? name}-container`}
            >
                <DynamicIcon
                    dataTestId={`${id ?? name}`}
                    width={width}
                    height={height}
                    color={color}
                />
            </Box>
        );
    }

    return null;
};
